import { lazy, Suspense, useState, useCallback, useEffect, useMemo } from "react";
import { useCurrentUser } from "./hooks/useCitizenship";
import { useActiveWars } from "./hooks/useWars";
import { useKeyboardShortcuts } from "./hooks/useKeyboardShortcuts";
import { useIsMobile } from "./hooks/useMediaQuery";
import { useOnboarding } from "./hooks/useOnboarding";
import { useQuestLog } from "./hooks/useQuestLog";
import { useTutorialPlayer } from "./hooks/useTutorialPlayer";
import { useMiniApp } from "./hooks/useMiniApp";
import { ApiError, auth, clearAuthToken } from "./lib/api";
import { getE2EConfig } from "./lib/e2e";
import { pageToHash, hashToPage } from "./lib/router";
import type { Page } from "./lib/router";
import { getClientPlatform } from "./lib/platform";
import VersionCard from "./components/VersionCard";
import { ToastProvider, useToast } from "./components/shared/Toast";

const Landing = lazy(() => import("./pages/Landing"));
const IOSIntro = lazy(() => import("./pages/IOSIntro"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NationDetail = lazy(() => import("./pages/NationDetail"));
const WarDetail = lazy(() => import("./pages/WarDetail"));
const Profile = lazy(() => import("./pages/Profile"));
const CreateCoin = lazy(() => import("./pages/CreateCoin"));
const Docs = lazy(() => import("./pages/Docs"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const Updates = lazy(() => import("./pages/Updates"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const FeedbackWidget = lazy(() => import("./components/shared/FeedbackWidget"));
const Navbar = lazy(() => import("./components/layout/Navbar"));
const BottomNav = lazy(() => import("./components/layout/BottomNav"));
const NotificationToast = lazy(() => import("./components/shared/NotificationToast"));
const CommandPalette = lazy(() => import("./components/shared/CommandPalette"));
const OnboardingOverlay = lazy(() => import("./components/onboarding/OnboardingOverlay"));
const OnboardingBeacon = lazy(() => import("./components/onboarding/OnboardingBeacon"));
const WelcomeCard = lazy(() => import("./components/onboarding/WelcomeCard"));
const QuestHUD = lazy(() => import("./components/onboarding/QuestHUD"));
const QuestSpotlight = lazy(() => import("./components/onboarding/QuestSpotlight"));
const TutorialPlayer = lazy(() => import("./components/onboarding/TutorialPlayer"));
const LoginBriefing = lazy(() => import("./components/idle/LoginBriefing"));
const LiveFeed = lazy(() => import("./components/shared/LiveFeed"));
const StatusBar = lazy(() => import("./components/shared/StatusBar"));
const DailyLoginModal = lazy(() => import("./components/rewards/DailyLoginModal"));
const PENDING_REFERRAL_KEY = "cw_pending_referral";

function normalizeReferralCode(value: string | null): string | null {
  if (!value) return null;
  const normalized = value.trim().toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20);
  return normalized || null;
}

function ShellFallback() {
  return (
    <div className="flex min-h-[100svh] items-center justify-center bg-nw-bg text-center">
      <div className="space-y-3 animate-flash-in">
        <div className="font-heading text-[2rem] font-black uppercase tracking-[0.18em] text-amber-500 sm:text-3xl">
          CLAWWARS
        </div>
        <div className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-500">
          Establishing command uplink
        </div>
      </div>
    </div>
  );
}

function getInitialMobileViewport() {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
}

function resolveInitialDashboardTab(isAuthed: boolean) {
  const initialPage = hashToPage(window.location.hash, isAuthed);
  if (initialPage.type === "dashboard" && initialPage.tab) {
    return initialPage.tab;
  }

  return getInitialMobileViewport() ? "war_map" : "hq";
}

function getSignedOutEntryPage(isIOSShell: boolean): Page {
  return isIOSShell ? { type: "ios-intro" } : { type: "landing" };
}

export default function App() {
  const e2eConfig = getE2EConfig();
  const e2eAuthBypass = !!e2eConfig?.authBypass;
  const platform = getClientPlatform();
  const isIOSShell = platform === "ios";
  const mobileBottomOffset = "calc(56px + env(safe-area-inset-bottom, 0px))";
  const [ageVerified, setAgeVerified] = useState(() => {
    if (e2eConfig?.state === "dashboard") {
      return true;
    }

    try { return localStorage.getItem("cw_age_verified") === "1"; } catch { return false; }
  });
  const { data: currentUser, isLoading: currentUserLoading, isFetched: currentUserFetched } = useCurrentUser(ageVerified);
  const { data: activeWars } = useActiveWars(ageVerified);
  const isAuthed = !!currentUser?.user || e2eAuthBypass;
  const authResolved = !ageVerified || e2eAuthBypass || currentUserFetched;
  const isMobile = useIsMobile();
  const onboarding = useOnboarding(isAuthed);
  const questLog = useQuestLog(isAuthed);
  const tutorial = useTutorialPlayer();

  // CW-501: Farcaster Mini-App SDK integration
  const miniApp = useMiniApp(ageVerified);

  // V1.3.1: Login briefing (show once per session)
  const [showBriefing, setShowBriefing] = useState(() => {
    if (e2eAuthBypass) return false;
    const key = "cw_briefing_shown";
    const shown = sessionStorage.getItem(key);
    if (shown) return false;
    sessionStorage.setItem(key, "1");
    return true;
  });

  // Hash-based routing — read initial page from URL hash
  const [page, setPage] = useState<Page>(() => {
    const hash = window.location.hash;
    if (hash) {
      const initialPage = hashToPage(hash, e2eAuthBypass);
      if (initialPage.type === "dashboard" && !initialPage.tab) {
        return { type: "dashboard", tab: resolveInitialDashboardTab(e2eAuthBypass) };
      }
      return initialPage;
    }
    return e2eAuthBypass
      ? { type: "dashboard", tab: resolveInitialDashboardTab(true) }
      : getSignedOutEntryPage(isIOSShell);
  });
  const [liveFeedOpen, setLiveFeedOpen] = useState(() => {
    try { return localStorage.getItem("cw-livefeed") !== "false"; } catch { return true; }
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const toggleLiveFeed = useCallback(() => {
    setLiveFeedOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem("cw-livefeed", String(next)); } catch {}
      return next;
    });
  }, []);
  const [dashboardTab, setDashboardTab] = useState<string | null>(() => {
    return resolveInitialDashboardTab(e2eAuthBypass);
  });
  const defaultDashboardTab = isMobile ? "war_map" : "hq";
  const [authTransition, setAuthTransition] = useState(false);
  const pageKey = useMemo(() => {
    switch (page.type) {
      case "dashboard":
        return `dashboard:${page.tab || dashboardTab || defaultDashboardTab}`;
      case "nation":
        return `nation:${page.code}`;
      case "war":
        return `war:${page.id}`;
      default:
        return page.type;
    }
  }, [dashboardTab, defaultDashboardTab, page]);

  // Navigate function — updates state AND URL hash
  const navigate = useCallback((p: Page) => {
    const nextPage = p.type === "dashboard" && !p.tab
      ? { ...p, tab: dashboardTab || defaultDashboardTab }
      : p;

    setMobileSidebarOpen(false);
    setPage(nextPage);
    window.scrollTo(0, 0);
    const hash = pageToHash(nextPage);
    if (hash) {
      window.history.pushState(null, "", hash);
    } else {
      // Landing — remove hash
      window.history.pushState(null, "", window.location.pathname);
    }
  }, [dashboardTab, defaultDashboardTab]);

  // Listen for browser back/forward and in-app hash navigation.
  useEffect(() => {
    const handlePopState = () => {
      const hash = window.location.hash;
      const newPage = hashToPage(hash, isAuthed);
      if (newPage.type === "dashboard" && !newPage.tab) {
        setPage({ type: "dashboard", tab: dashboardTab || defaultDashboardTab });
        return;
      }
      setPage(newPage);
    };
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("hashchange", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("hashchange", handlePopState);
    };
  }, [dashboardTab, defaultDashboardTab, isAuthed]);

  useEffect(() => {
    if (!authResolved || authTransition) {
      return;
    }

    const requestedPage = hashToPage(window.location.hash, isAuthed);
    const normalizedRequested: Page =
      requestedPage.type === "dashboard" && !requestedPage.tab
        ? { type: "dashboard", tab: dashboardTab || defaultDashboardTab }
        : requestedPage;

    if (!isAuthed) {
      const publicSignedOutPages = isIOSShell
        ? ["ios-intro", "docs", "updates", "privacy", "terms"]
        : ["landing", "docs", "updates", "privacy", "terms"];

      if (!publicSignedOutPages.includes(page.type) || (!isIOSShell && page.type === "ios-intro") || (isIOSShell && page.type === "landing")) {
        setPage(getSignedOutEntryPage(isIOSShell));
      }
      return;
    }

    const requestedTab =
      normalizedRequested.type === "dashboard"
        ? normalizedRequested.tab || defaultDashboardTab
        : null;

    if (requestedTab) {
      setDashboardTab((prev) => (prev === requestedTab ? prev : requestedTab));
    }

    if (page.type === "landing") {
      setPage(normalizedRequested);
    }
  }, [authResolved, authTransition, dashboardTab, defaultDashboardTab, isAuthed, isIOSShell, page.type]);

  useEffect(() => {
    const handleAuthExpired = () => {
      clearAuthToken();
      setMobileSidebarOpen(false);
      setDashboardTab(defaultDashboardTab);
      setPage(getSignedOutEntryPage(isIOSShell));
      setAuthTransition(false);
    };

    window.addEventListener("clawwars:auth-expired", handleAuthExpired as EventListener);
    return () => {
      window.removeEventListener("clawwars:auth-expired", handleAuthExpired as EventListener);
    };
  }, [defaultDashboardTab, isIOSShell]);

  useEffect(() => {
    setMobileSidebarOpen(false);
  }, [pageKey]);

  const wars = activeWars || [];
  const activeWarCount = wars.length;
  const hasActiveNuke = wars.some(
    (w: any) => w.warType === "nuke" && (w.status === "countdown" || w.status === "active")
  );

  const { commandPaletteOpen, setCommandPaletteOpen } = useKeyboardShortcuts({
    navigate,
  });

  // Quest context threading
  useEffect(() => {
    questLog.updateContext({
      hasNation: !!currentUser?.nation,
      currentPage: page.type,
      activeTab: page.type === "dashboard" ? dashboardTab : null,
    });
  }, [currentUser?.nation, page.type, dashboardTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (commandPaletteOpen) {
      questLog.updateContext({ commandPaletteOpened: true });
    }
  }, [commandPaletteOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQuestTabReport = useCallback((tab: string) => {
    setDashboardTab(tab);
  }, []);

  useEffect(() => {
    if (page.type !== "dashboard") {
      return;
    }

    const resolvedTab = page.tab || defaultDashboardTab;
    if (dashboardTab !== resolvedTab) {
      setDashboardTab(resolvedTab);
    }

    if (page.tab === resolvedTab) {
      return;
    }

    const nextPage: Page = { type: "dashboard", tab: resolvedTab };
    setPage(nextPage);
    window.history.replaceState(null, "", pageToHash(nextPage));
  }, [dashboardTab, defaultDashboardTab, page]);

  const confirmAge = useCallback(() => {
    try { localStorage.setItem("cw_age_verified", "1"); } catch {}
    setAgeVerified(true);
  }, []);

  useEffect(() => {
    try {
      const current = new URL(window.location.href);
      const referral = normalizeReferralCode(current.searchParams.get("ref"));
      if (!referral) {
        return;
      }

      sessionStorage.setItem(PENDING_REFERRAL_KEY, referral);
      current.searchParams.delete("ref");
      const nextUrl = `${current.pathname}${current.search}${current.hash}`;
      window.history.replaceState(null, "", nextUrl);
    } catch {
      // ignore malformed URL/storage failures
    }
  }, []);

  useEffect(() => {
    if (!isAuthed || !currentUser?.user) {
      return;
    }

    const pendingReferral = normalizeReferralCode(sessionStorage.getItem(PENDING_REFERRAL_KEY));
    if (!pendingReferral) {
      return;
    }

    if (currentUser.user.referredByUserId || currentUser.user.referralCode === pendingReferral) {
      sessionStorage.removeItem(PENDING_REFERRAL_KEY);
      return;
    }

    let cancelled = false;

    auth.applyReferral(pendingReferral)
      .then(() => {
        if (!cancelled) {
          sessionStorage.removeItem(PENDING_REFERRAL_KEY);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && [400, 404, 409].includes(err.status)) {
          sessionStorage.removeItem(PENDING_REFERRAL_KEY);
        }
        console.warn("[Referral] Failed to apply referral code", err);
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser?.user, isAuthed]);

  const handleAuth = useCallback(() => {
    setAuthTransition(true);
    setTimeout(() => {
      const nextPage: Page = { type: "dashboard", tab: defaultDashboardTab };
      setDashboardTab(defaultDashboardTab);
      setPage(nextPage);
      window.history.pushState(null, "", pageToHash(nextPage));
      setAuthTransition(false);
    }, 800);
  }, [defaultDashboardTab]);

  const syncDashboardTab = useCallback((tab: string, pushHistory = true) => {
    setMobileSidebarOpen(false);
    setDashboardTab(tab);
    const nextPage: Page = { type: "dashboard", tab };

    setPage((prev) => {
      if (prev.type === "dashboard" && prev.tab === tab) {
        return prev;
      }

      return nextPage;
    });

    const nextHash = pageToHash(nextPage);
    if (window.location.hash !== nextHash) {
      if (pushHistory) {
        window.history.pushState(null, "", nextHash);
      } else {
        window.history.replaceState(null, "", nextHash);
      }
    }
  }, []);

  // Handle tab change from BottomNav / Navbar / Dashboard
  const handleBottomNavTabChange = useCallback((tab: string) => {
    syncDashboardTab(tab);
  }, [syncDashboardTab]);

  useEffect(() => {
    const boot = document.getElementById("cw-boot");
    if (!boot) return;

    boot.classList.add("is-hidden");
    const timeout = window.setTimeout(() => boot.remove(), 240);
    return () => window.clearTimeout(timeout);
  }, []);

  const handleOpenMobileSidebar = useCallback(() => {
    setMobileSidebarOpen(true);
  }, []);

  // === PUBLIC PAGES (accessible without auth) ===
  const isPublicPage = ["ios-intro", "docs", "updates", "privacy", "terms"].includes(page.type);

  if (!isPublicPage && ageVerified && currentUserLoading) {
    return (
      <ToastProvider>
        <ShellFallback />
      </ToastProvider>
    );
  }

  // Docs page — publicly accessible
  if (page.type === "docs") {
    return (
      <ToastProvider>
        <Suspense fallback={<ShellFallback />}>
          <div className="min-h-screen bg-nw-bg text-zinc-100">
            <Navbar
              user={currentUser?.user}
              nation={currentUser?.nation}
              navigate={navigate}
              activeWarCount={activeWarCount}
              hasActiveNuke={hasActiveNuke}
              currentPage={page.type}
              currentDashboardTab={dashboardTab}
            />
            <Docs navigate={navigate} />
            <NotificationToast />
            <CommandPalette
              isOpen={commandPaletteOpen}
              onClose={() => setCommandPaletteOpen(false)}
              navigate={navigate}
            />
            <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/5 bg-[#0A0A0A]/90 backdrop-blur-sm">
              <div className="mx-auto max-w-6xl px-4 py-2 flex items-center justify-between">
                <span className="font-mono text-ui-xs lg:text-ui-sm text-white/20">&copy; 2026 ClawWars</span>
                <VersionCard navigate={navigate} />
              </div>
            </div>
          </div>
        </Suspense>
      </ToastProvider>
    );
  }

  // Updates page — publicly accessible
  if (page.type === "updates") {
    return (
      <ToastProvider>
        <Suspense fallback={<ShellFallback />}>
          <div className="min-h-screen bg-nw-bg text-zinc-100">
            <Navbar
              user={currentUser?.user}
              nation={currentUser?.nation}
              navigate={navigate}
              activeWarCount={activeWarCount}
              hasActiveNuke={hasActiveNuke}
              currentPage={page.type}
              currentDashboardTab={dashboardTab}
            />
            <Updates navigate={navigate} />
            <NotificationToast />
            <CommandPalette
              isOpen={commandPaletteOpen}
              onClose={() => setCommandPaletteOpen(false)}
              navigate={navigate}
            />
            <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/5 bg-[#0A0A0A]/90 backdrop-blur-sm">
              <div className="mx-auto max-w-6xl px-4 py-2 flex items-center justify-between">
                <span className="font-mono text-ui-xs lg:text-ui-sm text-white/20">&copy; 2026 ClawWars</span>
                <VersionCard navigate={navigate} />
              </div>
            </div>
          </div>
        </Suspense>
      </ToastProvider>
    );
  }

  // Privacy Policy — publicly accessible
  if (page.type === "privacy") {
    return (
      <ToastProvider>
        <Suspense fallback={<ShellFallback />}>
          <PrivacyPolicy />
        </Suspense>
      </ToastProvider>
    );
  }

  // Terms of Service — publicly accessible
  if (page.type === "terms") {
    return (
      <ToastProvider>
        <Suspense fallback={<ShellFallback />}>
          <Terms />
        </Suspense>
      </ToastProvider>
    );
  }

  // Auth transition screen
  if (authTransition) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-nw-bg">
        <div className="animate-flash-in text-center">
          <div className="font-heading text-2xl font-black uppercase tracking-widest text-amber-500">
            Clearance Granted
          </div>
          <div className="mt-2 font-mono text-ui-xs md:text-ui-sm uppercase tracking-wider text-zinc-500">
            Entering command center...
          </div>
          <div className="mx-auto mt-4 h-0.5 w-32 overflow-hidden rounded bg-zinc-800">
            <div className="h-full w-full animate-pulse bg-amber-500/70" />
          </div>
        </div>
      </div>
    );
  }

  // === AGE GATE (Apple App Store § 5.1.1) ===
  if (!ageVerified) {
    return (
      <div className="flex min-h-[100svh] flex-col items-center justify-start bg-nw-bg px-5 pb-10 pt-[max(3.5rem,calc(env(safe-area-inset-top,0px)+2rem))] text-center sm:justify-center sm:px-6 sm:py-10">
        <div className="w-full max-w-sm space-y-4 sm:space-y-5">
          <div className="font-heading text-[1.85rem] font-black uppercase tracking-[0.18em] text-amber-500 sm:text-3xl">
            CLAWWARS
          </div>
          <div className="space-y-3 rounded-xl border border-zinc-800 bg-nw-surface/95 p-5 shadow-[0_18px_50px_rgba(0,0,0,0.45)] sm:space-y-4 sm:p-6">
            <div className="mx-auto inline-flex rounded-full border border-amber-500/20 bg-amber-500/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400">
              17+ only
            </div>
            <h2 className="font-heading text-xl font-bold text-zinc-100">Age Verification</h2>
            <p className="font-mono text-sm leading-6 text-zinc-300 sm:text-[15px]">
              CLAWWARS is rated 17+ and contains simulated warfare, strategic competition, and virtual currency mechanics. By continuing, you confirm you are at least 17 years old.
            </p>
            <button
              onClick={confirmAge}
              aria-label="Confirm age gate"
              className="w-full rounded-lg bg-amber-600 px-6 py-3.5 font-heading text-sm font-bold uppercase tracking-wider text-black transition-colors hover:bg-amber-500 cursor-pointer"
            >
              I am 17 or older
            </button>
            <p className="mx-auto max-w-[18rem] font-mono text-xs leading-5 text-zinc-400 sm:text-[11px]">
              By continuing you agree to our{" "}
              <a href="#terms" className="text-amber-400 transition-colors hover:text-amber-300">Terms of Service</a>
              {" "}and{" "}
              <a href="#privacy" className="text-amber-400 transition-colors hover:text-amber-300">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // === LANDING PAGE (not authenticated) ===
  if (!isAuthed) {
    if (page.type === "ios-intro" && isIOSShell) {
      return (
        <ToastProvider>
          <Suspense fallback={<ShellFallback />}>
            <IOSIntro onAuth={handleAuth} navigate={navigate} />
          </Suspense>
        </ToastProvider>
      );
    }

    return (
      <ToastProvider>
        <Suspense fallback={<ShellFallback />}>
          <div className="relative">
            <Landing onAuth={handleAuth} navigate={navigate} />
            <div className="fixed bottom-0 left-0 right-0 z-40 hidden border-t border-white/5 bg-[#0A0A0A]/90 backdrop-blur-sm md:block">
              <div className="mx-auto max-w-6xl px-4 py-2 flex items-center justify-between">
                <span className="font-mono text-ui-xs lg:text-ui-sm text-white/20">&copy; 2026 ClawWars</span>
                <VersionCard navigate={navigate} />
              </div>
            </div>
          </div>
        </Suspense>
      </ToastProvider>
    );
  }

  // === AUTHENTICATED PAGES ===
  return (
    <ToastProvider>
    <Suspense fallback={<ShellFallback />}>
    <div
      className={`min-h-screen bg-nw-bg text-zinc-100 ${isMobile ? "pb-[calc(56px+env(safe-area-inset-bottom,0px))]" : ""}`}
      style={{ ["--cw-mobile-nav-offset" as string]: mobileBottomOffset }}
    >
      {/* Live Feed Sidebar — desktop only */}
      {!isMobile && (
        <div className={`fixed right-0 top-0 z-40 h-screen transition-all duration-300 ${liveFeedOpen ? "w-72" : "w-0"}`}>
          <LiveFeed
            collapsed={!liveFeedOpen}
            onToggle={toggleLiveFeed}
          />
        </div>
      )}
      {/* Main content area — shrinks when feed is open */}
      <div className={`transition-all duration-300 ${!isMobile && liveFeedOpen ? "mr-72" : ""}`}>
      <Navbar
        user={currentUser?.user}
        nation={currentUser?.nation}
        navigate={navigate}
        activeWarCount={activeWarCount}
        hasActiveNuke={hasActiveNuke}
        isMiniApp={miniApp.isMiniApp}
        onMobileTabChange={handleBottomNavTabChange}
        showMobileMenu
        mobileMenuOpen={mobileSidebarOpen}
        onMobileMenuOpenChange={setMobileSidebarOpen}
        currentPage={page.type}
        currentDashboardTab={dashboardTab}
      />
      {page.type === "dashboard" && (
        <Dashboard
          navigate={navigate}
          onTabChange={handleBottomNavTabChange}
          activeTabOverride={dashboardTab}
          onRestartOnboarding={onboarding.restart}
          onQuestTabReport={handleQuestTabReport}
          miniApp={miniApp}
        />
      )}
      {page.type === "nation" && (
        <NationDetail code={page.code} navigate={navigate} />
      )}
      {page.type === "war" && (
        <WarDetail warId={page.id} navigate={navigate} />
      )}
      {page.type === "profile" && <Profile navigate={navigate} />}
      {page.type === "create-coin" && <CreateCoin navigate={navigate} />}
      {page.type === "admin" && currentUser?.user?.role === "admin" && (
        <AdminDashboard navigate={navigate} />
      )}
      {page.type === "admin" && currentUser?.user?.role !== "admin" && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <span className="font-mono text-lg text-red-500">ACCESS DENIED</span>
          <button
            onClick={() => navigate({ type: "dashboard" })}
            className="font-mono text-sm text-amber-500 hover:underline"
          >
            Return to Dashboard
          </button>
        </div>
      )}
      {page.type === "not-found" && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <span className="font-mono text-4xl text-zinc-600">404</span>
          <span className="font-mono text-sm text-zinc-500">Page not found</span>
          <button
            onClick={() => navigate({ type: "dashboard" })}
            className="font-mono text-sm text-amber-500 hover:underline"
          >
            Return to Dashboard
          </button>
        </div>
      )}

      {/* Bottom navigation — mobile only */}
      {isMobile && (
        <BottomNav
          navigate={navigate}
          activePage={page.type}
          activeDashboardTab={page.type === "dashboard" ? dashboardTab : null}
          activeWarCount={activeWarCount}
          onOpenSidebar={handleOpenMobileSidebar}
          onTabChange={handleBottomNavTabChange}
        />
      )}

      {/* Global overlays */}
      <NotificationToast />
      <CommandPalette
        isOpen={commandPaletteOpen}
        onClose={() => setCommandPaletteOpen(false)}
        navigate={navigate}
        onRestartOnboarding={onboarding.restart}
        onOpenTutorial={tutorial.open}
      />

      {/* Quest-based onboarding */}
      <WelcomeCard
        visible={questLog.phase === "welcome"}
        onBegin={questLog.dismissWelcome}
        onSkip={questLog.skipAll}
      />
      {questLog.phase === "active" && (
        <QuestSpotlight
          targetSelector={questLog.activeQuest?.spotlightTarget ?? null}
          hint={questLog.activeQuest?.spotlightHint ?? ""}
          active={!questLog.celebrationActive && !!questLog.activeQuest}
          keyboardHint={questLog.activeQuest?.id === "command"}
          onDismiss={() => {
            if (questLog.activeQuest?.id === "command") {
              questLog.updateContext({ commandPaletteOpened: true });
            }
          }}
        />
      )}
      <QuestHUD
        activeQuest={questLog.activeQuest}
        completedCount={questLog.completedIds.length}
        totalQuests={questLog.totalQuests}
        progress={questLog.progress}
        isMinimized={questLog.isMinimized}
        onToggleMinimize={questLog.toggleMinimize}
        celebrationActive={questLog.celebrationActive}
        lastCompletedReward={questLog.lastCompletedReward}
        allComplete={questLog.phase === "complete"}
        onDismiss={questLog.dismiss}
        onOpenTutorial={tutorial.open}
        hasWatchedTutorial={tutorial.hasWatched}
      />

      {/* Old onboarding overlay — available via "Replay Briefing" */}
      {onboarding.isActive && (
        <OnboardingOverlay
          isActive={onboarding.isActive}
          step={onboarding.currentStep}
          stepIndex={onboarding.stepIndex}
          totalSteps={onboarding.totalSteps}
          onNext={onboarding.next}
          onPrev={onboarding.prev}
          onSkip={onboarding.skip}
        />
      )}
      <OnboardingBeacon
        visible={onboarding.showBeacon}
        onRestart={onboarding.restart}
        onDismiss={onboarding.dismissBeacon}
      />

      {/* V1.3.1: Login briefing overlay */}
      {showBriefing && (
        <LoginBriefing onDismiss={() => setShowBriefing(false)} />
      )}

      {/* V2.0: Daily login reward modal */}
      <DailyLoginModal />

      {/* Status Bar Footer — replaces old version footer */}
      {!isMobile && <StatusBar navigate={navigate} />}

      {/* Live animated tutorial player */}
      <TutorialPlayer
        isOpen={tutorial.isOpen}
        currentSlide={tutorial.currentSlide}
        isPlaying={tutorial.isPlaying}
        hasWatched={tutorial.hasWatched}
        totalSlides={tutorial.totalSlides}
        progress={tutorial.progress}
        slideElapsed={tutorial.slideElapsed}
        onOpen={tutorial.open}
        onClose={tutorial.close}
        onPlay={tutorial.play}
        onPause={tutorial.pause}
        onNext={tutorial.nextSlide}
        onPrev={tutorial.prevSlide}
        onRestart={tutorial.restart}
        hideButton={isMobile || questLog.phase === "complete"}
      />

      {/* Feedback Widget — always visible */}
      <FeedbackWidget />
      </div>{/* end main content area */}
    </div>
    </Suspense>
    </ToastProvider>
  );
}
